import { useNavigate } from 'react-router';
import hspaDoc from '@assets/docs/2019-05-28_HSAP_ODM_Form_10238.pdf';
import { useGlobalStore } from '@services/store/global-store';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@see-ui/core';
import { GlobeAltIcon, PhoneIcon } from '@heroicons/react/outline';
import { useState } from 'react';
import { useConfig } from '@components/ConfigProvider';
import { searchMember } from '@services/api/member-service';
import { ROUTES } from '@config/routes-config';

const NavLink = () => {
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { userDetails, preference, isNC } = useGlobalStore();
  const { state } = useGlobalStore(
    (state) => state.userDetails.userInformation
  );
  const config = useConfig();
  const openStateUrls = () => {
    const externalURLs = {
      PROVIDER_FINDER_URL: config.VITE_PROVIDER_FINDER_URL,
      PROVIDER_FINDER_URL_GA: config.VITE_PROVIDER_FINDER_URL_GA,
      PROVIDER_FINDER_URL_NC: config.VITE_PROVIDER_FINDER_URL_NC
    };
    if (isNC) {
      const url = externalURLs.PROVIDER_FINDER_URL_NC;
      return url;
    } else {
      const url =
        state === 'GA'
          ? externalURLs.PROVIDER_FINDER_URL_GA
          : externalURLs.PROVIDER_FINDER_URL;
      return url;
    }
  };

  const handleContactUsClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    setIsDialogOpen(true);
  };
  const hasAssocAdminRole =
    userDetails?.userInformation?.roleStateInfo?.some(
      (item) => item?.role === 'assocAdmin'
    ) ?? false;

  const isSuperAdmin = userDetails?.userInformation?.isSuperAdmin ?? false;

  const manageUsers =
    userDetails?.userInformation?.userType === 'INTERNAL' ||
    userDetails?.userInformation?.userRole === 'user_admin';
  const allowedLinks: string[] = [];
  if (manageUsers) {
    if (hasAssocAdminRole) {
      allowedLinks.push('manageUsers');
    }
  }
  if (isSuperAdmin) {
    allowedLinks.push('manageInternalUsers');
  }
  const NavLinksArray = [
    {
      id: 'dashboard',
      name: 'Dashboard',
      //  dataAnalytics: 'DashbordLink',
      url: () => navigate(ROUTES.DASHBOARD),
      withinApp: true
    },
    {
      id: 'reportCriticalIncident',
      name: 'Report A Critical Incident',
      dataAnalytics: 'reportCriticalIncidentLink',
      url: 'https://providers.anthem.com/ohio-provider/patient-care/report-an-incident',
      target: '_blank',
      withinApp: false
    },
    {
      id: 'healthSafetyActionPlan',
      name: 'Health & Safety Action Plan',
      dataAnalytics: 'healthSafetyActionPlan',
      url: hspaDoc,
      target: '_blank'
    },
    {
      id: 'communityResourceLink',
      name: 'Community Resource Link',
      dataAnalytics: 'communityResourceLink',
      url: 'https://anthemoh.findhelp.com/',
      target: '_blank',
      withinApp: false
    },
    {
      id: 'manageUsers',
      name: 'Manage Users',
      dataAnalytics: 'DashbordLink',
      url: () => navigate(ROUTES.ADMIN),
      withinApp: true
    },
    {
      id: 'findCare',
      name: 'Find Care',
      dataAnalytics: 'findCareLink',
      url: openStateUrls(),
      target: '_blank',
      withinApp: false
    },
    {
      id: 'toolsAndResources',
      name: 'Tools & Resources',
      dataAnalytics: 'toolsAndResourcesLink',
      url: () => navigate(ROUTES.TOOLSRESOURCES),
      withinApp: true
    },
    {
      id: 'ciiDashboard',
      name: 'Population Health Dashboard',
      dataAnalytics: 'ciiDashboardLink',
      url: () => navigate(ROUTES.CIIDASHBOARD),
      withinApp: true
    },
    {
      id: 'surveyLink',
      name: 'Give Us Your Feedback',
      dataAnalytics: 'surveyLink',
      url: () => navigate(ROUTES.SURVEY),
      withinApp: true
    },
    {
      id: 'enrollmentform',
      name: 'Enrollment Form',
      url: () => navigate(ROUTES.NEWENROLLMENT),
      withinApp: true
    },
    {
      id: 'calendar',
      name: 'View Calendar',
      url: () => navigate(ROUTES.CALENDAR),
      withinApp: true
    },
    {
      id: 'contactus',
      name: 'Contact Us',
      url: () => navigate(ROUTES.CONTACTUS),
      withinApp: true
    },
    {
      id: 'manageInternalUsers',
      name: 'Manage Internal Users',
      dataAnalytics: 'ManageInternalUsersLink',
      url: () => navigate(ROUTES.MANAGEINTERNALUSERS),
      withinApp: true
    }
  ];

  if (
    useGlobalStore.getState().userDetails.userInformation?.userRole === 'member'
  ) {
    NavLinksArray.unshift({
      id: 'memberOverview',
      name: 'Member Overview',
      url: () => navigateToMemberPage(),
      withinApp: true
    });
  }

  const navigateToMemberPage = async () => {
    const req = {
      medicaidId: useGlobalStore.getState().userDetails.userInformation?.ssoId,
      cceName: useGlobalStore.getState().userDetails.userInformation.cceName,
      state: useGlobalStore.getState().userDetails.userInformation.state,
      userType: useGlobalStore.getState().userDetails.userInformation.userType,
      county: useGlobalStore.getState().userDetails.userInformation.county
    };

    const response: any = await searchMember(req);
    if (response.status === 'SUCCESS' && response?.members?.length > 0) {
      if (response.members && response.members.length > 0) {
        navigate(ROUTES.MEMBERPAGE, {
          state: { memberDetails: response.members[0] }
        });
      }
    }
  };
  return (
    <>
      {NavLinksArray.map(
        (link) =>
          (preference?.header?.[link.id] || allowedLinks.includes(link.id)) && (
            <div className="slim-nav-link text-sm pt-4" key={link.id}>
              {link.withinApp ? (
                <a
                  data-analytics={link.dataAnalytics}
                  className="focus:outline focus:outline-2 focus:outline-blue-500 focus:outline-offset-2 focus:rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-2 focus-visible:rounded"
                  onClick={(e) => {
                    e.preventDefault();
                    if (link.id === 'contactus') {
                      if (isNC && userDetails.userId) {
                        if (typeof link.url === 'function') {
                          link.url();
                        }
                      } else {
                        handleContactUsClick(e);
                      }
                    } else if (typeof link.url === 'function') {
                      link.url();
                    }
                  }}
                  href="#"
                >
                  {link.name}
                </a>
              ) : (
                <a
                  data-analytics={link.dataAnalytics}
                  href={typeof link.url === 'string' ? link.url : undefined}
                  target={link.target}
                  className="focus:outline focus:outline-2 focus:outline-blue-500 focus:outline-offset-2 focus:rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-2 focus-visible:rounded"
                >
                  {link.name}
                </a>
              )}
            </div>
          )
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild></DialogTrigger>
        <DialogContent style={{ maxWidth: '900px' }}>
          <DialogHeader>
            <DialogTitle className="mb-4 text-2xl">Contact Us</DialogTitle>
            <DialogDescription className="text-left text-black text-lg">
              Get the support you need so you can focus on providing member
              care.
            </DialogDescription>
          </DialogHeader>
          <div className="bordered-box">
            <div className="flex mb-4">
              <div className="flex-col" style={{ width: '40%' }}>
                <div style={{ display: 'flex' }}>
                  <PhoneIcon className="size-8 text-green-400 pr-3"></PhoneIcon>
                  Call Member Services at:{' '}
                </div>
              </div>

              <div className="flex-col" style={{ width: '70%' }}>
                <h6>844-912-0938 (TTY 711)</h6>
                <h6>
                  Monday through Friday from 7 a.m. to 8 p.m. Eastern time.
                </h6>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <GlobeAltIcon className="size-8 text-green-400 pr-3"></GlobeAltIcon>
              <h6>
                For assistance with website issues, please call: 866-755-2680
              </h6>
            </div>
          </div>
          <DialogFooter className="md:justify-end">
            <DialogClose asChild>
              <Button type="button" variant="primary">
                Close
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NavLink;
